import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { card, h1, currency, fmtDate } from "./ui";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight, Loader2, Wallet, Clock3, CalendarCheck, Check, CalendarClock } from "lucide-react";

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const mLabel = (key) => { const [y, m] = key.split("-"); return `${MONTHS[+m - 1]}/${y.slice(2)}`; };
const MONTHS_FULL = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const mFull = (key) => { const [y, m] = key.split("-"); const t = `${MONTHS_FULL[+m - 1]} de ${y}`; return t.charAt(0).toUpperCase() + t.slice(1); };

export default function Financeiro() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/overview?month=${month}`);
      setData(data);
    } catch {
      toast.error("Não consegui carregar o financeiro.");
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const shift = (d) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(new Date(Date.UTC(y, m - 1 + d, 1)).toISOString().slice(0, 7));
  };

  const markPaid = async (sid) => {
    setPayingId(sid);
    try {
      await api.patch(`/sessions/${sid}/payment`, { status: "pago" });
      toast.success("Pagamento registrado.");
      load();
    } catch { toast.error("Não consegui atualizar o pagamento."); }
    finally { setPayingId(null); }
  };

  return (
    <div data-testid="finance-page" className="max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Financeiro</p>
          <h1 className={h1}>Painel financeiro</h1>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full bg-[#F5F0E6] p-1" data-testid="finance-month-nav">
          <button onClick={() => shift(-1)} data-testid="finance-prev-month" className="grid place-items-center w-8 h-8 rounded-full text-[#5B6E5B] hover:bg-white transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-body text-sm text-[#2C332A] px-2 min-w-[130px] text-center" data-testid="finance-month-label">{mFull(month)}</span>
          <button onClick={() => shift(1)} data-testid="finance-next-month" className="grid place-items-center w-8 h-8 rounded-full text-[#5B6E5B] hover:bg-white transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {loading || !data ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B] mt-10" data-testid="finance-loading" />
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`${card} p-5`} data-testid="finance-received-card">
              <p className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#5B6E5B]"><Wallet className="w-4 h-4" /> Recebido no mês</p>
              <p className="font-display text-3xl text-[#2C332A] mt-2" data-testid="finance-received">{currency(data.received)}</p>
            </div>
            <div className={`${card} p-5`} data-testid="finance-pending-card">
              <p className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#C87A5E]"><Clock3 className="w-4 h-4" /> A receber no mês</p>
              <p className="font-display text-3xl text-[#2C332A] mt-2" data-testid="finance-pending">{currency(data.pending)}</p>
              <p className="font-body text-xs text-[#A8B0A2] mt-1">sessões já passadas sem baixa</p>
            </div>
            <div className={`${card} p-5`} data-testid="finance-forecast-card">
              <p className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#7A8A7A]"><CalendarClock className="w-4 h-4" /> Previsto no mês</p>
              <p className="font-display text-3xl text-[#2C332A] mt-2" data-testid="finance-forecast">{currency(data.forecast)}</p>
              <p className="font-body text-xs text-[#A8B0A2] mt-1">sessões futuras agendadas</p>
            </div>
            <div className={`${card} p-5`} data-testid="finance-sessions-card">
              <p className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#7A8A7A]"><CalendarCheck className="w-4 h-4" /> Sessões no mês</p>
              <p className="font-display text-3xl text-[#2C332A] mt-2" data-testid="finance-session-count">{data.session_count}</p>
            </div>
          </div>

          <div className={`${card} p-5 sm:p-6 mt-6`} data-testid="finance-chart-card">
            <p className="font-display text-xl text-[#2C332A] mb-4">Evolução dos últimos 6 meses</p>
            <div className="h-64">
              {mounted && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.series.map((s) => ({ ...s, label: mLabel(s.month) }))} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4DCCB" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#7A8A7A" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#7A8A7A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${v}`} />
                  <Tooltip formatter={(v, name) => [currency(v), name === "received" ? "Recebido" : "A receber"]}
                    contentStyle={{ borderRadius: 12, border: "1px solid #E4DCCB", background: "#FDFBF7", fontSize: 13 }} />
                  <Legend formatter={(v) => (v === "received" ? "Recebido" : "A receber")} wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="received" fill="#5B6E5B" radius={[6, 6, 0, 0]} maxBarSize={34} />
                  <Bar dataKey="pending" fill="#C87A5E" radius={[6, 6, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mt-6 items-start">
            <div className={`${card} p-5 sm:p-6`} data-testid="finance-per-patient">
              <p className="font-display text-xl text-[#2C332A] mb-4">Resumo por paciente (geral)</p>
              {data.per_patient.length === 0 ? (
                <p className="font-body text-sm text-[#7A8A7A]">Nenhuma sessão registrada ainda.</p>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-3 pb-2 border-b border-[#5B6E5B]/10 font-body text-xs uppercase tracking-wider text-[#7A8A7A]">
                    <span>Paciente</span><span className="text-right">Recebido</span><span className="text-right">Pendente</span>
                  </div>
                  {data.per_patient.map((p) => (
                    <div key={p.patient_name} className="grid grid-cols-[1fr_auto_auto] gap-3 py-2 border-b border-[#5B6E5B]/5 last:border-0 font-body text-sm" data-testid="finance-patient-row">
                      <span className="text-[#2C332A] truncate">{p.patient_name} <span className="text-xs text-[#7A8A7A]">({p.sessions} sessões)</span></span>
                      <span className="text-right text-[#4A5D4E]">{currency(p.paid)}</span>
                      <span className={`text-right ${p.pending > 0 ? "text-[#C87A5E] font-medium" : "text-[#7A8A7A]"}`}>{currency(p.pending)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={`${card} p-5 sm:p-6`} data-testid="finance-pending-list">
              <p className="font-display text-xl text-[#2C332A] mb-4">Pagamentos pendentes</p>
              {data.pending_list.length === 0 ? (
                <p className="font-body text-sm text-[#7A8A7A]">Nada pendente. Tudo em dia! 💚</p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {data.pending_list.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 rounded-xl bg-[#F5F0E6]/60 px-3.5 py-2.5" data-testid="finance-pending-row">
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm text-[#2C332A] truncate">{s.patient_name}</p>
                        <p className="font-body text-xs text-[#7A8A7A]">{fmtDate(s.date)} às {s.time} · {currency(s.price)}</p>
                      </div>
                      <button onClick={() => markPaid(s.id)} disabled={payingId === s.id} data-testid={`finance-mark-paid-${s.id}`}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#5B6E5B] px-3 py-1.5 text-xs text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors disabled:opacity-60">
                        {payingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Recebido
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
